/**
 * How long a household's record is kept.
 *
 * One setting, in one place, because this number appears in three others: the
 * sentence a household reads before agreeing to it, the period recorded on the
 * consent row as what was agreed, and whatever eventually enforces it. Written
 * out separately in each, they would disagree within a release, and the one
 * that matters legally is the sentence somebody was shown.
 *
 * It is null, which means no fixed period: the record is kept until the
 * household closes the account. That is deliberately not the same as a promise
 * to keep it forever, and the copy below does not make one.
 *
 * WHY NULL RATHER THAN A NUMBER. Nothing Asvise has published commits to a
 * period and the framework asks for the consent without naming one, so any
 * figure here would be mine rather than the company's. A previous version of
 * this said 24 months, in the consent copy and in a migration comment, and
 * nothing anywhere acted on it. A commitment nobody enforces is worse than no
 * commitment, because the household has been told something untrue rather than
 * told less.
 *
 * WHY IT IS A SETTING AND NOT A DELETED FEATURE. Setting a period later is a
 * reduction in what is kept, and a household that agreed to an open-ended
 * period has already agreed to anything shorter. So this can be changed to a
 * number without asking anybody again, which is the direction that is safe.
 * Going the other way, from a stated period to a longer one, is a new thing to
 * agree to and needs the consent asked for again. `periodOf` on each consent
 * row is what makes that answerable rather than guessed at.
 *
 * ONE PLACE IS NOT YET THE DATABASE. When the purge exists it has to read the
 * same figure. If it runs in Postgres on a schedule, the number has to be in a
 * migration too and a test has to assert the two agree; if it runs as a script
 * or a worker, it imports this. That choice is open, which is exactly why this
 * is a module and not a literal buried in a sentence.
 */
export const RETENTION_MONTHS: number | null = null

/**
 * The title and body a household reads, built from the setting.
 *
 * Both wordings are written out in full rather than assembled from fragments.
 * A sentence stitched together from a number and a clause reads like one, and
 * this is the sentence somebody is agreeing to.
 */
/**
 * The sentence, and nothing but the sentence.
 *
 * It returned a title and a line about what withdrawing would stop, for a
 * consent row with a heading and a checkbox. There is no such row: the basis is
 * the terms, nothing asks and nothing can be taken back here, and the only
 * reader left is the record of what wording was in force. So what is produced
 * is what is stored.
 */
export function retentionCopy(months: number | null = RETENTION_MONTHS): { body: string } {
  if (months === null) {
    return {
      body: 'We keep what you have entered so you can come back to it, and we delete it when you close your account. There is no fixed period at the moment: if we set one, we will tell you what it is before it applies to your record. Deleting is not instant everywhere: backups and logs hold copies for a limited period before they are overwritten.',
    }
  }

  return {
    body: `We keep what you have entered for ${months} months from your last visit, so you can come back to it, and delete it when you close your account. After that it is removed without you having to ask. Deleting is not instant everywhere: backups and logs hold copies for a limited period before they are overwritten.`,
  }
}

/**
 * What a consent row records about the period, so a row says what was agreed.
 *
 * The privacy policy undertakes to record the scope, time and status of a
 * consent. For retention, the scope is the period, and recording it is what
 * lets a change be handled correctly later: a row saying `months: null` was an
 * agreement to an open-ended period, and a household holding one has not
 * agreed to anything a later figure says, in either direction. Without this,
 * changing the setting would silently restate what everybody had agreed to.
 */
export function retentionScope(months: number | null = RETENTION_MONTHS): {
  months: number | null
  basis: 'UNTIL_ACCOUNT_CLOSED' | 'FIXED_PERIOD_FROM_LAST_ACTIVITY'
} {
  return {
    months,
    basis: months === null ? 'UNTIL_ACCOUNT_CLOSED' : 'FIXED_PERIOD_FROM_LAST_ACTIVITY',
  }
}
